// Match Lab label review: Review mode and the Wizard as one queue (Ben,
// 2026-10-22). One pass per event type; each moment is ONE item carrying
// where it came from — the detector (L4), a human label (event_labels),
// and/or a ball-position candidate (wizard-candidates) — so a goal the
// detector found and someone labeled is judged once, not twice.
//
// Scope: "Existing" steps through what is already there (detections and
// labels); "+ Find more" adds the ball-position candidates nothing existing
// is near. "/" switches (F is the Foul / FK label shortcut). The keys mean the same on every item and do the right thing for
// its source:
//
//   Enter/A  confirm a detection (review) · mark a label checked · label a candidate
//   Bksp/R   reject a detection (review) · delete a label · dismiss a candidate
//   E        correct a detection · move a label · pick a candidate's frame
//   C        change team
//
// Writes are the existing ones: detection verdicts are event reviews
// (raw_assertion), labels are event_labels. "Checked" labels and dismissed
// candidates are this browser's memory (localStorage) — server-side
// dismissals need a migration (raw_assertion.target_type), deferred.
import type { CSSProperties, ReactNode } from 'react'

import type { PitchEvent } from './MatchLabPitch'
import { ReviewBadge, ReviewControls } from './MatchLabReview'
import {
  PERIOD_KICKOFF_S, possibleGoalKickOffs, POSSIBLE_GOAL_LOOKBACK_S, SCAN_RATES,
} from './MatchLabReviewMode'
import {
  NEAR_DETECTED_S, NEAR_LABEL_S, readRejected, writeRejected,
  type WizardLabel, type ZoneCandidate,
} from './MatchLabWizard'

export type LabelPass = 'goals' | 'shots' | 'corners' | 'throwins'
  | 'goalkicks' | 'kickoffs' | 'restarts'

export const LABEL_PASSES: { id: LabelPass; label: string }[] = [
  { id: 'goals', label: 'Goals' },
  { id: 'shots', label: 'Shots' },
  { id: 'corners', label: 'Corners' },
  { id: 'throwins', label: 'Throw-ins' },
  { id: 'goalkicks', label: 'Goal kicks' },
  { id: 'kickoffs', label: 'Kick-offs' },
  { id: 'restarts', label: 'Other restarts' },
]

export type Scope = 'existing' | 'more'
type Side = 'home' | 'away'
type Period = { period_id: number; start_frame_idx: number; end_frame_idx: number }

/** The label a candidate becomes when accepted (event_label vocabulary:
 *  restarts are labeled where they are TAKEN). */
export const PASS_WRITE_TYPE: Record<LabelPass, string | null> = {
  goals: 'goal', shots: 'shot', corners: 'corner',
  throwins: 'throw_in', goalkicks: 'goal_kick',
  kickoffs: 'kick_off', restarts: null,
}

/** Human labels this pass SHOWS as items: exactly the scorecard's truth
 *  types (event_eval.LABEL_EVENTS), so what is reviewed is what is scored. */
const PASS_LABEL_TYPES: Record<LabelPass, string[]> = {
  goals: ['goal'],
  shots: ['shot', 'shot_saved', 'shot_blocked', 'shot_off_target'],
  corners: ['corner'],
  throwins: ['throw_in'],
  goalkicks: ['goal_kick'],
  kickoffs: ['kick_off'],
  restarts: ['free_kick'],
}

/** Labels that make a ball-position candidate redundant: broader than the
 *  shown types (the wizard's rule — a stop_for_throw_in is the same throw-in, a
 *  a goal explains a six-yard-box dwell). */
const PASS_SUPPRESS_TYPES: Record<LabelPass, string[]> = {
  goals: ['goal'],
  shots: ['shot', 'shot_saved', 'shot_blocked', 'shot_off_target', 'goal'],
  corners: ['corner'],
  throwins: ['throw_in', 'stop_for_throw_in'],
  goalkicks: ['goal_kick', 'out_for_goal_kick', 'goal'],
  kickoffs: ['kick_off'],
  restarts: [],
}

const PASS_ZONE_KIND: Record<LabelPass, ZoneCandidate['kind'] | null> = {
  goals: 'goal', shots: null, corners: 'corner',
  throwins: 'throw_in', goalkicks: 'goal_kick', kickoffs: 'kick_off',
  restarts: null,
}

const ZONE_DETAIL: Record<ZoneCandidate['kind'], string> = {
  goal: 'ball on the goal mouth',
  corner: 'ball at the corner flag',
  throw_in: 'ball over the sideline',
  goal_kick: 'ball in the six-yard box',
  kick_off: 'ball on the centre spot',
}

/** What a ball-position candidate saw: a strong kick-off
 *  (wizard_candidates.strong_kick_offs) says both of its cues. */
export const zoneDetail = (z: ZoneCandidate) =>
  z.cue === 'both' ? 'ball on the centre spot, teams lined up in their halves'
    : z.cue === 'restart' ? 'teams lined up in their halves for a kick-off'
      : z.cue === 'crowd' ? 'players crowded in the box, standing still'
        : ZONE_DETAIL[z.kind]

/** A detection and a label this close are one moment: the scorecard's own
 *  match tolerances (event_eval.TOLERANCE_S), so a merged item is exactly
 *  a pair the scorecard would pair. */
export const MERGE_S: Record<LabelPass, number> = {
  goals: 3, shots: 1.5, corners: 3, throwins: 3, goalkicks: 3, kickoffs: 3,
  restarts: 3,
}

/** Clips: goals, shots and corners need what came after; restarts the
 *  moment itself (Review mode's 4 s / 3 s). */
const CLIP: Record<LabelPass, [number, number]> = {
  goals: [3, 7], shots: [3, 7], corners: [3, 7],
  throwins: [4, 3], goalkicks: [4, 3], kickoffs: [4, 3], restarts: [4, 3],
}

const LABEL_NAMES: Record<string, string> = {
  goal: 'Goal', shot: 'Shot', shot_saved: 'Shot (saved)',
  shot_blocked: 'Shot (blocked)', shot_off_target: 'Shot (off target)',
  corner: 'Corner', goal_kick: 'Goal kick', throw_in: 'Throw-in',
  free_kick: 'Free kick', kick_off: 'Kick-off',
}
export const labelName = (t: string) => LABEL_NAMES[t] ?? t

export type LabelItem = {
  key: string
  pass: LabelPass
  frame: number
  clipStart: number
  clipEnd: number
  /** Played at the scan speed (a possible-goal look-back). */
  fast: boolean
  detected: PitchEvent | null
  /** 'possible_goal': a kick-off with no goal before it — was one missed? */
  reason: 'detected' | 'possible_goal' | null
  label: WizardLabel | null
  candidate: ZoneCandidate | null
  suggestedSide: Side | null
  /** A detection and its merged label disagree on the team. */
  teamConflict: boolean
  /** No frame to write at: E must pick it first (a goal asked from a
   *  kick-off with no ball seen at a goal before it). */
  pickFrame?: boolean
}

export type QueueInputs = {
  events: PitchEvent[]
  labels: WizardLabel[]
  zones: ZoneCandidate[]
  periods: Period[]
  fps: number
  homeTeamId: number | null
  awayTeamId: number | null
}

function sideOf(teamId: number | null | undefined, home: number | null,
                away: number | null): Side | null {
  if (teamId == null) return null
  return teamId === home ? 'home' : teamId === away ? 'away' : null
}

const asSide = (s: string | null): Side | null =>
  s === 'home' || s === 'away' ? s : null

/** The kick-offs the Goals pass asks "was a goal missed?" about: no
 *  detected goal before them (possibleGoalKickOffs) AND no labeled one —
 *  a label already answers the question. One rule for both passes, so a
 *  kick-off is never dropped from both (249's f24033 was, once). */
function possibleGoals(events: PitchEvent[], labels: WizardLabel[],
                       periods: Period[], fps: number): PitchEvent[] {
  const lookback = POSSIBLE_GOAL_LOOKBACK_S * fps
  return possibleGoalKickOffs(events, periods, fps).filter((k) =>
    !labels.some((l) => l.event_type === 'goal' && l.frame <= k.frame_idx
                        && k.frame_idx - l.frame <= lookback))
}

function detectionsFor(pass: LabelPass, events: PitchEvent[],
                       labels: WizardLabel[], periods: Period[],
                       fps: number): PitchEvent[] {
  const restart = (d: string) => (e: PitchEvent) =>
    e.kind === 'set_piece' && e.detail === d
  switch (pass) {
    case 'goals': return events.filter((e) => e.kind === 'goal')
    case 'shots': return events.filter((e) => e.kind === 'shot')
    case 'corners': return events.filter(restart('corner'))
    case 'throwins': return events.filter(restart('throw_in'))
    case 'goalkicks': return events.filter(restart('goal_kick'))
    case 'kickoffs': {
      // A kick-off already asked about in Goals ("was a goal missed?") is
      // judged there, not twice.
      const inGoals = new Set(possibleGoals(events, labels, periods, fps)
        .map((e) => e.key))
      return events.filter((e) => restart('kick_off')(e) && !inGoals.has(e.key))
    }
    case 'restarts': return events.filter((e) => e.kind === 'set_piece'
      && ['free_kick', 'penalty'].includes(e.detail ?? ''))
  }
}

/** Everything a pass has, merged into one item per moment, in match order.
 *  Candidates are always computed (the tab counts them); scope 'existing'
 *  leaves them out of the queue. */
export function buildLabelQueue(pass: LabelPass, inp: QueueInputs,
                                scope: Scope): LabelItem[] {
  const { events, labels, periods, fps, homeTeamId, awayTeamId } = inp
  const [preS, postS] = CLIP[pass]
  const pre = Math.round(preS * fps)
  const post = Math.round(postS * fps)
  const merge = MERGE_S[pass] * fps
  const shownLabels = labels.filter((l) => PASS_LABEL_TYPES[pass].includes(l.event_type))

  const items: LabelItem[] = []
  const used = new Set<number>()

  // Detections, each taking the nearest unused label within the merge
  // window (greedy by distance, as the scorecard pairs them).
  const dets = detectionsFor(pass, events, labels, periods, fps)
  const pairs: { d: PitchEvent; l: WizardLabel; dist: number }[] = []
  for (const d of dets) {
    for (const l of shownLabels) {
      const dist = Math.abs(l.frame - d.frame_idx)
      if (dist <= merge) pairs.push({ d, l, dist })
    }
  }
  pairs.sort((a, b) => a.dist - b.dist)
  const paired = new Map<string, WizardLabel>()
  for (const p of pairs) {
    if (paired.has(p.d.key) || used.has(p.l.id)) continue
    paired.set(p.d.key, p.l)
    used.add(p.l.id)
  }
  for (const d of dets) {
    const l = paired.get(d.key) ?? null
    const dSide = sideOf(d.team_id, homeTeamId, awayTeamId)
    const lSide = l ? asSide(l.side) : null
    items.push({
      key: d.key, pass, frame: d.frame_idx,
      clipStart: Math.max(0, d.frame_idx - pre), clipEnd: d.frame_idx + post,
      fast: false, detected: d, reason: 'detected', label: l, candidate: null,
      suggestedSide: dSide ?? lSide,
      teamConflict: dSide != null && lSide != null && dSide !== lSide,
    })
  }

  // Goals: a kick-off with no goal before it may follow a missed one.
  // A labeled goal in that stretch answers the question too.
  if (pass === 'goals') {
    const lookback = POSSIBLE_GOAL_LOOKBACK_S * fps
    for (const k of possibleGoals(events, labels, periods, fps)) {
      items.push({
        key: k.key, pass, frame: k.frame_idx,
        clipStart: Math.max(0, k.frame_idx - lookback), clipEnd: k.frame_idx + post,
        fast: true, detected: k, reason: 'possible_goal', label: null,
        candidate: null, suggestedSide: null, teamConflict: false,
      })
    }
  }

  // Goals: a STRONG kick-off candidate with no goal before it asks the
  // same question, at the goal the ball was last seen at (Ben, 2026-10-22).
  if (pass === 'goals' && scope === 'more') items.push(...goalsFromKickOffs(inp))

  // Labels no detection took.
  for (const l of shownLabels) {
    if (used.has(l.id)) continue
    items.push({
      key: `label-${l.id}`, pass, frame: l.frame,
      clipStart: Math.max(0, l.frame - pre), clipEnd: l.frame + post,
      fast: false, detected: null, reason: null, label: l, candidate: null,
      suggestedSide: asSide(l.side), teamConflict: false,
    })
  }

  // Ball-position candidates nothing existing is near (the wizard's rules:
  // 8 s from a detection's moment, 12 s from a label that covers it).
  if (scope === 'more') items.push(...candidatesFor(pass, inp, suppressors(pass, events, dets)))
  return items.sort((a, b) => a.frame - b.frame)
}

function candidatesFor(pass: LabelPass, inp: QueueInputs,
                       dets: PitchEvent[]): LabelItem[] {
  const kind = PASS_ZONE_KIND[pass]
  if (kind == null) return []
  const { labels, zones, fps } = inp
  const nearDet = NEAR_DETECTED_S * fps
  const nearLab = NEAR_LABEL_S * fps
  const suppress = PASS_SUPPRESS_TYPES[pass]
  // A restart is labeled where it is TAKEN: the end of the ball's
  // out-of-play window. A goal or corner moment is its start.
  const restart = kind === 'throw_in' || kind === 'goal_kick'
    || kind === 'kick_off'
  const out: LabelItem[] = []
  for (const z of zones) {
    if (z.kind !== kind) continue
    if (dets.some((e) => e.frame_idx >= z.frame - nearDet
                         && e.frame_idx <= z.end_frame + nearDet)) continue
    const at = restart ? z.end_frame : z.frame
    if (labels.some((l) => suppress.includes(l.event_type)
                           && Math.abs(l.frame - at) <= nearLab)) continue
    out.push({
      // The wizard's key, so its earlier rejects carry over.
      key: `wiz-zone-${kind}-${z.frame}`, pass, frame: at,
      clipStart: z.clip_start, clipEnd: z.clip_end, fast: false,
      detected: null, reason: null, label: null, candidate: z,
      suggestedSide: z.side, teamConflict: false,
    })
  }
  return out
}

/** Detections that make a candidate redundant: the pass's own, plus —
 *  for kick-offs — those judged in the Goals pass: still the same moment.
 *  A detection the operator REJECTED says nothing happened there, so it
 *  hides nothing: the ball-position candidate comes back (the wizard's
 *  rule; SDET #854). */
function suppressors(pass: LabelPass, events: PitchEvent[],
                     dets: PitchEvent[]): PitchEvent[] {
  const all = pass === 'kickoffs'
    ? events.filter((e) => e.kind === 'set_piece' && e.detail === 'kick_off')
    : dets
  return all.filter((e) => e.review?.action !== 'reject')
}

/** The label a move writes: the same type and side at the new frame. A
 *  label with no side stays without one — an unsided throw-in moved was
 *  written back as HOME's, and the scorecard credited home (SDET #854). */
export function movedLabel(l: WizardLabel, frame: number): {
  frame: number; event_type: string; side: Side | null
} {
  return { frame, event_type: l.event_type, side: asSide(l.side) }
}

/** Who the kick-off's two cues say scored, in words (the panel's line on
 *  a goal asked from a strong kick-off). */
export function scorerGuess(z: ZoneCandidate,
                            name: (s: Side | null) => string): string {
  const where = z.goal_frame != null
    ? 'The ball was last at a goal before it'
    : 'No ball was seen at a goal before it — watch, pause on the goal, press E'
  if (z.cue === 'restart') {
    // read from the players alone: no kicker, one cue at most
    const seen = z.goal_frame != null
      ? 'The teams lined up for a kick-off; the ball was last in a penalty area before it — watch, pause on the goal, press E'
      : where
    return z.scorer ? `${seen}. ${name(z.scorer)} scored? (by the goal end; check the team.)`
      : `${seen}. No guess at the team.`
  }
  if (z.scorer) {
    return `${where}. ${name(z.scorer)} scored? `
      + `(${name(z.side ?? null)} kicked off; the goal end agrees.)`
  }
  if (z.scorer_by_end && z.scorer_by_kicker) {
    return `${where}. The cues disagree — the goal end says `
      + `${name(z.scorer_by_end)}, the kick-off says ${name(z.scorer_by_kicker)}: `
      + 'pick with C.'
  }
  const one = z.scorer_by_end ?? z.scorer_by_kicker
  return one ? `${where}. Only one cue: ${name(one)} scored? Check the team.`
    : `${where}. No guess at the team.`
}

/** Whether Enter has a frame to write `item`'s label at. A goal asked from
 *  a kick-off with no ball seen at a goal has none until E picks one away
 *  from the kick-off it opened on — E then Enter unmoved would label a
 *  goal AT the kick-off (SDET #868). */
export function hasFrameToWrite(item: Pick<LabelItem, 'pickFrame' | 'frame'>,
                                itemEdit: boolean, currentFrame: number): boolean {
  return !item.pickFrame || (itemEdit && currentFrame !== item.frame)
}

/** How many candidates "+ Find more" would add to a pass. */
export function candidateCount(pass: LabelPass, inp: QueueInputs): number {
  const dets = detectionsFor(pass, inp.events, inp.labels, inp.periods, inp.fps)
  return candidatesFor(pass, inp, suppressors(pass, inp.events, dets)).length
    + (pass === 'goals' ? goalsFromKickOffs(inp).length : 0)
}

/** How far before a kick-off a goal may be: 249's second goal was kicked
 *  off 125 s after it (wizard_candidates.GOAL_BEFORE_KICKOFF_S). */
export const GOAL_BEFORE_KICKOFF_S = 150

/** "Was a goal missed?" for each kick-off candidate read from a line-up —
 *  strong (both cues) or a restart (the players alone) — that
 *  does not open a period and has no goal before it — detected (and not
 *  rejected) or labeled — and that no detected kick-off already asks about.
 *  At the last ball seen at a goal, with the scorer the two cues agree on;
 *  with no such ball, the GOAL_BEFORE_KICKOFF_S before the kick at scan speed, and E picks
 *  the frame. */
function goalsFromKickOffs(inp: QueueInputs): LabelItem[] {
  const { events, labels, zones, periods, fps } = inp
  const back = GOAL_BEFORE_KICKOFF_S * fps
  const opening = PERIOD_KICKOFF_S * fps
  const asked = possibleGoals(events, labels, periods, fps)
  const near = NEAR_DETECTED_S * fps
  const out: LabelItem[] = []
  for (const z of zones) {
    if (z.kind !== 'kick_off' || (z.cue !== 'both' && z.cue !== 'restart')) continue
    const kick = z.end_frame
    if (periods.some((p) => kick >= p.start_frame_idx
                            && kick - p.start_frame_idx <= opening)) continue
    const before = (f: number) => f <= kick && kick - f <= back
    if (events.some((e) => e.kind === 'goal' && e.review?.action !== 'reject'
                           && before(e.frame_idx))) continue
    if (labels.some((l) => l.event_type === 'goal' && before(l.frame))) continue
    if (asked.some((k) => Math.abs(k.frame_idx - kick) <= near)) continue
    const g = z.goal_frame ?? null
    // The server's window from before the estimate to the kick: the
    // estimate is 8-20 s off, so the operator always scans and marks the
    // goal with E (KNOWLEDGE §45: a 6 s/4 s clip around it held 2 of 29
    // goals — a tight trust-the-frame clip is never offered). The server
    // sends goal_clip_* exactly when goal_frame is set (goal_clip);
    // without either, the whole window a goal was looked for in — a goal
    // 90-150 s back was asked about but outside a 90 s clip (SDET #868).
    const window = z.goal_clip_start != null && z.goal_clip_end != null
    out.push({
      key: `wiz-ko-goal-${z.frame}`, pass: 'goals', frame: g ?? kick,
      clipStart: window ? z.goal_clip_start! : Math.max(0, kick - back),
      clipEnd: window ? z.goal_clip_end! : kick,
      fast: true, detected: null, reason: 'possible_goal',
      label: null, candidate: z, suggestedSide: z.scorer ?? null,
      teamConflict: false, pickFrame: true,
    })
  }
  return out
}

/** Judged: a detection has a standing review; a label-only item has been
 *  checked here; a candidate has been dismissed here (an accepted one
 *  becomes a label and leaves the candidate list). */
export function isDone(item: LabelItem, checked: Set<number>,
                       dismissed: Set<string>): boolean {
  if (item.detected) return !!item.detected.review
  if (item.label) return checked.has(item.label.id)
  return dismissed.has(item.key)
}

/** The next open item after `fromKey` (or from the start), wrapping round
 *  to pick up any skipped earlier; null when the pass is done. */
export function nextOpen(queue: LabelItem[], fromKey: string | null,
                         checked: Set<number>,
                         dismissed: Set<string>): LabelItem | null {
  const at = fromKey == null ? -1 : queue.findIndex((q) => q.key === fromKey)
  const order = [...queue.slice(at + 1), ...queue.slice(0, Math.max(at, 0))]
  return order.find((q) => !isDone(q, checked, dismissed) && q.key !== fromKey)
    ?? null
}

// ── this browser's memory: checked labels, dismissed candidates ──

const checkedKey = (gameId: number) => `matchlab-review-checked-${gameId}`

export function readChecked(gameId: number): Set<number> {
  try {
    const raw = localStorage.getItem(checkedKey(gameId))
    const arr: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr)
      ? arr.filter((v): v is number => typeof v === 'number') : [])
  } catch {
    return new Set()
  }
}

export function writeChecked(gameId: number, checked: Set<number>) {
  try {
    localStorage.setItem(checkedKey(gameId), JSON.stringify([...checked]))
  } catch { /* storage full / disabled: checks just do not persist */ }
}

/** Dismissed candidates share the wizard's store, so its rejects carry over. */
export const readDismissed = readRejected
export const writeDismissed = writeRejected

// ── panel ──

const box: CSSProperties = {
  background: '#1a1a1e', color: '#e0e0e0', borderRadius: '4px',
  padding: '6px 10px', marginBottom: '4px', fontSize: '11px',
  border: '1px solid #2b4a8a',
}
const btn: CSSProperties = {
  background: 'none', border: '1px solid #444', borderRadius: '3px',
  color: '#bbb', fontSize: '10px', padding: '1px 6px', cursor: 'pointer',
}
const bigBtn: CSSProperties = {
  ...btn, fontSize: '11px', padding: '3px 10px', fontWeight: 600,
}
const KEY: CSSProperties = {
  background: '#333', padding: '0 4px', borderRadius: '2px', marginRight: '3px',
}
const tag = (bg: string): CSSProperties => ({
  fontSize: '9px', fontWeight: 600, padding: '0 4px', borderRadius: '2px',
  background: bg, color: '#fff', marginLeft: '4px',
})

export function LabelModePanel({
  pass, onPass, scope, onScope, queues, moreCounts, checked, dismissed,
  current, editing, side, onAccept, onReject, onEdit, onFlip,
  onNext, onPrev, onReplay, onGo, onExit, scan, onScan, scanRate, onScanRate,
  reviewMode, onReviewMode, onReviewSubmit, currentFrame, homeTeamId,
  awayTeamId, homeName, awayName, minute, available, loading, error, scorecard,
}: {
  pass: LabelPass
  onPass: (p: LabelPass) => void
  scope: Scope
  onScope: (s: Scope) => void
  queues: Record<LabelPass, LabelItem[]>
  /** Candidates "+ Find more" would add, per pass. */
  moreCounts: Record<LabelPass, number>
  checked: Set<number>
  dismissed: Set<string>
  current: LabelItem | null
  /** Frame-picking mode for a label or candidate (E). */
  editing: boolean
  /** The side an accept / move writes. */
  side: Side
  onAccept: () => void
  onReject: () => void
  onEdit: (on: boolean) => void
  onFlip: () => void
  onNext: () => void
  onPrev: () => void
  onReplay: () => void
  onGo: (item: LabelItem) => void
  onExit: () => void
  scan: boolean
  onScan: (on: boolean) => void
  scanRate: number
  onScanRate: (r: number) => void
  /** The detection review's inline form, for the current item. */
  reviewMode: 'reject' | 'correct' | null
  onReviewMode: (m: 'reject' | 'correct' | null) => void
  onReviewSubmit: (body: Record<string, unknown>) => void
  currentFrame: number
  homeTeamId: number | null
  awayTeamId: number | null
  homeName: string
  awayName: string
  minute: (frame: number) => string
  /** False when the game has no readable ball track (no candidates). */
  available: boolean
  /** The labels and candidates are still being fetched. */
  loading: boolean
  error: string | null
  scorecard: ReactNode
}) {
  const queue = queues[pass]
  const done = queue.filter((q) => isDone(q, checked, dismissed)).length
  const pos = current ? queue.findIndex((q) => q.key === current.key) : -1
  const sideName = (s: Side | null) =>
    s === 'home' ? homeName : s === 'away' ? awayName : '?'
  const team = (id: number | null) =>
    id === homeTeamId ? homeName : id === awayTeamId ? awayName : '?'
  const writeType = PASS_WRITE_TYPE[pass]

  const what = (it: LabelItem) =>
    it.reason === 'possible_goal'
      ? 'Kick-off with no goal before it — was a goal missed?'
      : it.detected ? it.detected.label
      : it.label ? labelName(it.label.event_type)
      : `${labelName(writeType ?? '')}? — ${zoneDetail(it.candidate!)}`

  return (
    <div style={box} role="region" aria-label="Label review">
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, marginRight: '4px' }}>Review</span>
          {LABEL_PASSES.map((p) => {
            const q = queues[p.id]
            const n = q.filter((it) => isDone(it, checked, dismissed)).length
            const more = scope === 'existing' ? moreCounts[p.id] : 0
            return (
              <button key={p.id} onClick={() => onPass(p.id)}
                      aria-pressed={pass === p.id}
                      style={{ ...btn, background: pass === p.id ? '#2563eb' : 'none',
                               color: pass === p.id ? '#fff' : '#bbb' }}>
                {p.label} {n}/{q.length}
                {more > 0 && <span style={{ opacity: 0.7 }}> · +{more}</span>}
              </button>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <div role="group" aria-label="Scope" style={{ display: 'flex' }}>
            {(['existing', 'more'] as const).map((s) => (
              <button key={s} onClick={() => onScope(s)} aria-pressed={scope === s}
                      title={s === 'more'
                        ? 'Also show ball-position candidates nothing existing is near (/)'
                        : 'Only detections and labels that already exist (/)'}
                      style={{ ...btn, borderRadius: s === 'existing' ? '3px 0 0 3px' : '0 3px 3px 0',
                               background: scope === s ? '#6d28d9' : 'none',
                               color: scope === s ? '#fff' : '#bbb' }}>
                {s === 'existing' ? 'Existing' : '+ Find more'}
              </button>
            ))}
          </div>
          <label title="Play the stretch between moments fast instead of jumping"
                 style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            <input type="checkbox" checked={scan}
                   onChange={(e) => onScan(e.target.checked)} />
            Scan
          </label>
          <select aria-label="Scan speed" value={scanRate}
                  onChange={(e) => onScanRate(Number(e.target.value))}
                  style={{ background: '#333', color: '#ccc', border: '1px solid #555',
                           fontSize: '10px' }}>
            {SCAN_RATES.map((r) => <option key={r} value={r}>{r}x</option>)}
          </select>
          <button style={btn} onClick={onExit}>Exit review (Esc)</button>
        </div>
      </div>

      <div style={{ height: '4px', background: '#333', borderRadius: '2px',
                    margin: '5px 0' }}>
        <div data-testid="label-progress"
             style={{ height: '100%', borderRadius: '2px', background: '#38a169',
                      width: queue.length ? `${(100 * done) / queue.length}%` : '0' }} />
      </div>

      {scope === 'more' && !available && (
        <div style={{ opacity: 0.7, marginBottom: '3px' }}>
          No ball track for this game: no candidates to find.
        </div>
      )}
      {error && (
        <div style={{ color: '#e53e3e', marginBottom: '3px' }}>
          Labels and candidates unavailable: {error}
        </div>
      )}

      {loading ? (
        <div style={{ opacity: 0.8 }}>Loading labels and candidates{'…'}</div>
      ) : current ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'flex-start', gap: '8px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>
                {pos + 1}/{queue.length} · {minute(current.frame)} · {what(current)}
                {' '}<span style={{ opacity: 0.6, fontWeight: 400 }}>
                  {current.detected && current.reason === 'detected'
                    ? team(current.detected.team_id)
                    : current.label || current.candidate ? sideName(side) : ''}
                </span>
                {current.detected && current.reason === 'detected'
                  && <span style={tag('#2563eb')}>Detected</span>}
                {current.label && <span style={tag('#38a169')}>Labeled</span>}
                {current.candidate && <span style={tag('#6d28d9')}>Candidate</span>}
                {current.detected && <>{' '}<ReviewBadge event={current.detected} /></>}
                {current.label && !current.detected && checked.has(current.label.id)
                  && <span style={{ fontSize: '9px', color: '#38a169' }}> Checked</span>}
                {current.candidate && dismissed.has(current.key)
                  && <span style={{ fontSize: '9px', color: '#e53e3e' }}> Dismissed</span>}
              </div>
              {current.teamConflict && (
                <div style={{ color: '#d69e2e', marginTop: '2px' }}>
                  The label says {sideName(asSide(current.label!.side))}; the
                  detection says {team(current.detected!.team_id)}.
                </div>
              )}
              {current.reason === 'possible_goal' && current.candidate && (
                <div style={{ marginTop: '2px' }}>
                  {scorerGuess(current.candidate, sideName)}
                </div>
              )}
              <div style={{ opacity: 0.6, marginTop: '2px' }}>
                {current.reason === 'possible_goal' && current.candidate
                  ? <><kbd style={KEY}>Enter</kbd>{current.pickFrame
                        ? 'label the goal at the frame E picked'
                        : 'label the goal here'}
                      {' '}<kbd style={KEY}>Bksp</kbd>no goal
                      {' '}<kbd style={KEY}>E</kbd>pick the frame <kbd style={KEY}>C</kbd>change team</>
                  : current.reason === 'possible_goal'
                  ? <>Watching the {POSSIBLE_GOAL_LOOKBACK_S} s before it. A goal
                      you see: pause on the line crossing and press <kbd style={KEY}>G</kbd>
                      (Shift for away). Then <kbd style={KEY}>Enter</kbd> confirms the kick-off.</>
                  : current.detected
                    ? <><kbd style={KEY}>Enter</kbd>confirm <kbd style={KEY}>Bksp</kbd>reject
                        {' '}<kbd style={KEY}>E</kbd>correct <kbd style={KEY}>C</kbd>change team</>
                    : current.label
                      ? <><kbd style={KEY}>Enter</kbd>checked <kbd style={KEY}>Bksp</kbd>delete
                          {' '}<kbd style={KEY}>E</kbd>move to this frame <kbd style={KEY}>C</kbd>change team</>
                      : <><kbd style={KEY}>Enter</kbd>label it <kbd style={KEY}>Bksp</kbd>dismiss
                          {' '}<kbd style={KEY}>E</kbd>pick the frame <kbd style={KEY}>C</kbd>change team</>}
                {' '}<kbd style={KEY}>Q</kbd>replay <kbd style={KEY}>,</kbd><kbd style={KEY}>.</kbd>prev / next
                {' '}<kbd style={KEY}>/</kbd>existing / find more
              </div>
            </div>
            <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
              <button style={btn} onClick={onPrev} title="Previous (,)">{'‹'}</button>
              <button style={btn} onClick={onReplay} title="Replay (Q)">{'↻'}</button>
              <button style={btn} onClick={onNext} title="Next (.)">{'›'}</button>
            </div>
          </div>

          {current.detected ? (
            <ReviewControls event={current.detected} mode={reviewMode} onMode={onReviewMode}
                            onSubmit={onReviewSubmit} currentFrame={currentFrame}
                            homeTeamId={homeTeamId} awayTeamId={awayTeamId}
                            homeName={homeName} awayName={awayName} />
          ) : (
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap',
                          alignItems: 'center' }}>
              <button style={{ ...bigBtn, color: '#38a169', borderColor: '#38a169' }}
                      onClick={onAccept}>
                {current.label ? 'Checked (A)' : `Label ${labelName(writeType ?? '')} (A)`}
              </button>
              <button style={{ ...bigBtn, color: '#e53e3e', borderColor: '#e53e3e' }}
                      onClick={onReject}>
                {current.label ? 'Delete label (R)' : 'Dismiss (R)'}
              </button>
              <button style={{ ...bigBtn, background: editing ? '#2563eb' : 'none',
                               color: editing ? '#fff' : '#bbb' }}
                      onClick={() => onEdit(!editing)}>
                {current.label ? 'Move frame (E)' : 'Pick frame (E)'}
              </button>
              <button style={bigBtn} onClick={onFlip}>Change team (C)</button>
              {editing && (
                <span style={{ opacity: 0.8 }}>
                  Step to the moment (arrows), then <kbd style={KEY}>Enter</kbd>
                  {current.label ? ' moves the label' : ' labels it'} at f{currentFrame}.
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        <div style={{ opacity: 0.8 }}>
          {queue.length === 0
            ? (scope === 'existing' && moreCounts[pass] > 0
              ? <>Nothing existing in this pass. <button style={btn} onClick={() => onScope('more')}>
                  + Find more ({moreCounts[pass]})</button></>
              : 'Nothing to review in this pass.')
            : done < queue.length
              ? (<>{queue.length - done} still to judge in this pass.{' '}
                  <button style={btn}
                          onClick={() => onGo(nextOpen(queue, null, checked, dismissed)!)}>
                    Resume</button></>)
            : (<>Everything in this pass is judged.{' '}
                <button style={btn} onClick={() => onGo(queue[0]!)}>Go through again</button>
                {scope === 'existing' && moreCounts[pass] > 0 && (
                  <>{' '}<button style={btn} onClick={() => onScope('more')}>
                    + Find more ({moreCounts[pass]})</button></>
                )}</>)}
        </div>
      )}
      {!loading && (!current || done === queue.length) && scorecard}
    </div>
  )
}
